/**
 * LiveScaleModal — live weight capture for Staff POS. (Iter 82/83)
 *
 * Features:
 *  - Streams weight from the paired scale (BLE / USB-Serial / HID).
 *  - Optional **auto-capture on stable hold** — when the reading is
 *    "stable" for N seconds (3 / 5 / 10, user pick), the highest
 *    reading seen during the hold window is captured and returned
 *    to the caller. Manual "Use" button still works alongside.
 *  - Stability is determined by (a) the scale's own stable flag if
 *    it emits one, OR (b) client-side variance guard — every new
 *    reading within ±10 g of the peak during the window keeps the
 *    countdown alive; anything outside restarts it.
 *
 * Props:
 *  - open: boolean
 *  - onClose(): void
 *  - onUse(kg: number): void
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Scales, X, CheckCircle, ArrowsClockwise, Warning, Lightning, PauseCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  startScaleStream, disconnectScale, whyScaleUnavailable, lastDeviceLabel,
  getAutoCapture, setAutoCapture, getHoldSeconds, setHoldSeconds,
} from "../../lib/scaleReader";

// Any two readings this close (kg) count as "the same" for hold logic.
const STABLE_TOLERANCE_KG = 0.010; // 10 grams — enough for most 20 kg bag scales
const MIN_CAPTURE_KG      = 0.020; // ignore <20 g so an empty pan doesn't trigger
const HOLD_OPTIONS        = [3, 5, 10];

export default function LiveScaleModal({ open, onClose, onUse }) {
  // Live-stream state
  const [reading, setReading] = useState(null);
  const [status, setStatus] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [err, setErr] = useState(null);

  // Hold-to-capture state
  const [autoCap, setAutoCapState] = useState(getAutoCapture());
  const [holdSec, setHoldSecState] = useState(getHoldSeconds());
  const [peak, setPeak]           = useState(0);      // highest kg during current hold
  const [holdStart, setHoldStart] = useState(null);   // ts when stability started
  const [holdMs, setHoldMs]       = useState(0);      // elapsed ms (for progress bar)
  const [captured, setCaptured]   = useState(false);  // guard against double-fire

  const stopRef  = useRef(null);
  const readingRef = useRef(null);   // latest reading
  const peakRef  = useRef(0);        // running peak during current hold
  const holdStartRef = useRef(null); // ts when hold began
  const settingsRef = useRef({ autoCap, holdSec }); // avoids re-subscribe
  const capturedRef = useRef(false);

  const why = open ? whyScaleUnavailable() : null;

  // Keep refs in sync so the RAF ticker + reading callback always see current
  // values without triggering a re-subscribe.
  useEffect(() => { settingsRef.current = { autoCap, holdSec }; }, [autoCap, holdSec]);

  const stop = async () => {
    if (stopRef.current) {
      try { await stopRef.current(); } catch (_) { /* noop */ }
      stopRef.current = null;
    }
    setStreaming(false);
  };

  const resetHold = () => {
    holdStartRef.current = null;
    peakRef.current = 0;
    setHoldStart(null);
    setHoldMs(0);
    setPeak(0);
  };

  const start = async () => {
    setErr(null);
    setStreaming(true);
    setCaptured(false); capturedRef.current = false;
    resetHold();
    try {
      const s = await startScaleStream({
        transport: "auto",
        onReading: (r) => onReadingTick(r),
        onStatus:  (m) => setStatus(m),
      });
      stopRef.current = s;
    } catch (e) {
      setErr(e?.message || "Could not connect");
      setStreaming(false);
    }
  };

  /**
   * Every incoming reading updates:
   *   - `reading` for display
   *   - `peak`    (highest kg seen during the current hold window)
   *   - `holdStart` (kept alive if new reading is within tolerance of peak;
   *                  reset if it moves outside the tolerance band)
   *
   * The RAF ticker below actually advances `holdMs` and fires the capture
   * when it crosses the threshold.
   */
  const onReadingTick = (r) => {
    if (capturedRef.current) return; // already captured this session
    readingRef.current = r;
    setReading(r);

    const kg = Number(r.kg);
    if (!Number.isFinite(kg) || kg < MIN_CAPTURE_KG) {
      // Empty pan / zero / negative — reset the hold, keep waiting.
      resetHold();
      return;
    }

    const currentPeak = peakRef.current;
    const withinBand = currentPeak === 0
      || Math.abs(kg - currentPeak) <= STABLE_TOLERANCE_KG;

    if (withinBand) {
      // Same reading zone — track the peak (handles "or higher" ask) and
      // keep the hold alive.
      const newPeak = Math.max(currentPeak, kg);
      peakRef.current = newPeak;
      setPeak(newPeak);
      if (holdStartRef.current == null) {
        const now = Date.now();
        holdStartRef.current = now;
        setHoldStart(now);
      }
    } else {
      // Reading jumped outside the tolerance — user probably added / removed
      // weight. Restart the hold anchored on the new value.
      peakRef.current = kg;
      const now = Date.now();
      holdStartRef.current = now;
      setPeak(kg);
      setHoldStart(now);
      setHoldMs(0);
    }
  };

  // RAF ticker — advances the countdown bar + fires capture when done.
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const tick = () => {
      if (!capturedRef.current) {
        const start = holdStartRef.current;
        if (start != null) {
          const ms = Date.now() - start;
          setHoldMs(ms);
          const { autoCap, holdSec } = settingsRef.current;
          if (autoCap && ms >= holdSec * 1000) {
            const capture = Math.max(peakRef.current, (readingRef.current?.kg || 0));
            if (capture >= MIN_CAPTURE_KG) {
              capturedRef.current = true;
              setCaptured(true);
              fireCapture(capture);
              return;
            }
          }
        } else if (holdMs !== 0) {
          setHoldMs(0);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fireCapture = async (kg) => {
    const rounded = Number(kg.toFixed(3));
    await stop();
    await disconnectScale();
    toast.success(`Captured ${rounded} kg`);
    onUse(rounded);
  };

  useEffect(() => {
    if (open && !why) start();
    return () => { stop(); disconnectScale(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const kg = reading ? Number(reading.kg) : 0;
  const canUse = reading && kg > 0;
  const holdPct = holdStart
    ? Math.min(100, (holdMs / (holdSec * 1000)) * 100)
    : 0;
  const holdRem = holdStart
    ? Math.max(0, holdSec - Math.floor(holdMs / 1000))
    : holdSec;

  const use = async () => {
    if (!canUse) { toast.error("Waiting for a valid reading"); return; }
    await stop(); await disconnectScale();
    onUse(Number(kg.toFixed(3)));
  };
  const cancel = async () => { await stop(); await disconnectScale(); onClose(); };

  const toggleAuto = () => {
    const next = !autoCap;
    setAutoCapState(next);
    setAutoCapture(next);
    resetHold();
  };
  const pickHold = (n) => {
    setHoldSecState(n);
    setHoldSeconds(n);
    resetHold();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3"
         onClick={cancel} data-testid="pos-live-scale">
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E5E2D9]">
          <div className="font-heading text-base inline-flex items-center gap-1.5">
            <Scales size={16} weight="fill" color="#1F3A57" />
            Read weight
          </div>
          <button onClick={cancel} className="p-1.5 hover:bg-[#F7F6F2] rounded" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {why && (
          <div className="p-4 text-sm text-[#7A2E18] bg-[#F9EDE7] border-b border-[#F4C7B9] flex items-start gap-2">
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <div>{why}</div>
          </div>
        )}

        {err && !why && (
          <div className="p-4 text-sm text-[#7A2E18] bg-[#F9EDE7] border-b border-[#F4C7B9] flex items-start gap-2"
               data-testid="pos-live-scale-error">
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <div>
              {err}
              <button onClick={start}
                      className="ml-2 underline text-[#0D47A1]"
                      data-testid="pos-live-scale-retry">
                Retry
              </button>
              <div className="mt-2 text-[11px] leading-snug text-[#7A2E18]/85">
                Can&apos;t see your scale? <strong>HC-05 / HC-06</strong> are Classic-Bluetooth and browsers can&apos;t read them —
                use an <strong>HM-10 / HC-08</strong> BLE module or plug the scale into a USB-serial adapter.
                See Scale settings for the full list.
              </div>
            </div>
          </div>
        )}

        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">
              {lastDeviceLabel() || "Scale"}
            </div>
            <button onClick={toggleAuto}
                    data-testid="pos-live-scale-auto-toggle"
                    className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded inline-flex items-center gap-1 ${
                      autoCap
                        ? "bg-[#D4E0D1] text-[#1A3317]"
                        : "bg-[#EEEBE2] text-[#5C5A55]"
                    }`}>
              {autoCap ? <Lightning size={11} weight="fill"/> : <PauseCircle size={11} weight="fill"/>}
              Auto-capture {autoCap ? "on" : "off"}
            </button>
          </div>

          {/* Hold-time chooser */}
          {autoCap && (
            <div className="mt-2 flex items-center gap-1">
              <span className="text-[10px] text-[#8A8780] mr-1 uppercase tracking-[0.14em]">Hold</span>
              {HOLD_OPTIONS.map(n => (
                <button key={n} onClick={() => pickHold(n)}
                        data-testid={`pos-live-scale-hold-${n}`}
                        className={`text-[10px] px-2 py-0.5 rounded border ${
                          holdSec === n
                            ? "bg-[#1F3A57] text-white border-[#1F3A57]"
                            : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#B7B3A6]"
                        }`}>
                  {n}s
                </button>
              ))}
            </div>
          )}

          <div className={`mt-3 rounded-md border-2 p-6 flex items-baseline gap-3 ${
              reading?.stable ? "border-[#2D5A27] bg-[#F0F7EE]" : "border-[#E5E2D9] bg-[#F7F6F2]"
            }`}>
            <div className="font-heading tabular-nums text-5xl" data-testid="pos-live-scale-value">
              {reading ? kg.toFixed(3) : "0.000"}
            </div>
            <div className="text-lg text-[#5C5A55]">kg</div>
            {reading?.stable && (
              <span className="ml-auto text-[10px] uppercase tracking-[0.18em] font-semibold text-[#2D5A27] bg-[#D4E0D1] px-1.5 py-0.5 rounded"
                    data-testid="pos-live-scale-stable">
                stable
              </span>
            )}
            {reading && !reading.stable && (
              <span className="ml-auto text-[10px] uppercase tracking-[0.18em] font-semibold text-[#7A5A11] bg-[#F9E4B7] px-1.5 py-0.5 rounded">
                moving
              </span>
            )}
          </div>

          {/* Countdown progress bar */}
          {autoCap && (
            <div className="mt-3" data-testid="pos-live-scale-hold-bar">
              <div className="h-1.5 rounded-full bg-[#EEEBE2] overflow-hidden">
                <div
                  className={`h-full transition-[width] duration-100 ${
                    holdPct >= 100 ? "bg-[#2D5A27]" : "bg-[#1F3A57]"
                  }`}
                  style={{ width: `${holdPct}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-[#5C5A55] flex items-center justify-between">
                <span>
                  {holdStart
                    ? `Holding steady… ${holdRem}s to auto-capture`
                    : reading
                      ? "Place the bag and hold steady"
                      : "Waiting for first reading…"}
                </span>
                {peak > 0 && (
                  <span className="tabular-nums text-[#1F3A57]">
                    Peak: {peak.toFixed(3)} kg
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="mt-2 text-[11px] text-[#5C5A55] flex items-center gap-2 min-h-[14px]">
            {streaming && !reading && (
              <>
                <ArrowsClockwise size={11} className="animate-spin"/>
                <span>{status || "Waiting for first reading…"}</span>
              </>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={cancel}
                    data-testid="pos-live-scale-cancel"
                    className="flex-1 h-11 border border-[#E5E2D9] rounded-md text-sm">
              Cancel
            </button>
            <button onClick={use} disabled={!canUse}
                    data-testid="pos-live-scale-use"
                    className="flex-1 h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <CheckCircle size={14} weight="fill"/>
              Use {reading ? `${kg.toFixed(3)} kg` : "reading"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
